
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Mail, Phone, Building, User } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { format, parseISO } from "date-fns";
import { formatPhoneNumber, getAvatarColor } from "@/lib/utils";

const statusColors = {
  new: "bg-slate-100 text-slate-700",
  contacted: "bg-purple-100 text-purple-700",
  qualifying: "bg-amber-100 text-amber-700",
  converted: "bg-emerald-100 text-emerald-700",
  lost: "bg-slate-100 text-slate-700"
};

export default function LeadTable({ 
  leads, 
  isLoading, 
  onViewLead, 
  visibleColumns = {}, 
  users = [],
  selectedLeadIds = [],
  onToggleSelect
}) {
  const getAssignedUserName = (email) => {
    if (!email) return null;
    const user = users.find(u => u.email === email);
    if (user && user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    } else if (user && user.full_name) {
      return user.full_name;
    }
    return email.split('@')[0];
  };

  const formatLocalDateTime = (dateString) => {
    if (!dateString) return null;
    
    // Parse the ISO date string and convert to local timezone
    const date = parseISO(dateString);
    return {
      date: format(date, "MMM d, yyyy"),
      time: format(date, "h:mm a")
    };
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array(8).fill(0).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto max-h-[calc(100dvh-264px)] md:max-h-[calc(100dvh-200px)] relative">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {onToggleSelect && (
                <th className="px-6 py-3 text-left bg-slate-50 sticky top-0 z-10 w-12">
                  <Checkbox
                    checked={leads.length > 0 && selectedLeadIds.length === leads.length}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        leads.forEach(lead => {
                          if (!selectedLeadIds.includes(lead.id)) {
                            onToggleSelect(lead.id);
                          }
                        });
                      } else {
                        selectedLeadIds.forEach(id => onToggleSelect(id));
                      }
                    }}
                  />
                </th>
              )}
              {visibleColumns.name && (
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider bg-slate-50 sticky top-0 z-10">
                  Name
                </th>
              )}
              {visibleColumns.contact && (
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider bg-slate-50 sticky top-0 z-10">
                  Contact
                </th>
              )}
              {visibleColumns.company && (
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider bg-slate-50 sticky top-0 z-10">
                  Company
                </th>
              )}
              {visibleColumns.status && (
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider bg-slate-50 sticky top-0 z-10">
                  Status
                </th>
              )}
              {visibleColumns.source && (
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider bg-slate-50 sticky top-0 z-10">
                  Source
                </th>
              )}
              {visibleColumns.assignedTo && (
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider bg-slate-50 sticky top-0 z-10">
                  Assigned To
                </th>
              )}
              {visibleColumns.createdBy && (
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider bg-slate-50 sticky top-0 z-10">
                  Created By
                </th>
              )}
              {visibleColumns.createdDate && (
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider bg-slate-50 sticky top-0 z-10">
                  Created Date
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {leads.map((lead) => {
              const createdDateTime = formatLocalDateTime(lead.created_date);
              const isSelected = selectedLeadIds.includes(lead.id);
              
              return (
                <tr
                  key={lead.id}
                  onClick={(e) => {
                    // Don't navigate if clicking checkbox
                    if (e.target.closest('[role="checkbox"]')) return;
                    
                    // Se for Ctrl/Cmd+Click, abre em nova aba
                    if (e.ctrlKey || e.metaKey) {
                      window.open(`/lead-detail?id=${lead.id}`, '_blank');
                      return;
                    }
                    
                    onViewLead(lead.id);
                  }}
                  className={`hover:bg-slate-50 cursor-pointer transition-colors ${
                    isSelected ? "bg-primary/5" : ""
                  }`}
                >
                  {onToggleSelect && (
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => onToggleSelect(lead.id)}
                      />
                    </td>
                  )}
                  {visibleColumns.name && (
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 bg-gradient-to-br ${getAvatarColor(lead.first_name)} rounded-full flex items-center justify-center flex-shrink-0`}>
                          <span className="text-white font-bold text-sm">
                            {lead.first_name.charAt(0)}{lead.last_name.charAt(0)}
                          </span>
                        </div>
                        <div className="flex flex-col gap-1">
                          <p className="font-semibold text-slate-900">
                            {lead.first_name} {lead.last_name}
                          </p>
                          {lead.market && (
                            <Badge 
                              variant="outline" 
                              className={`border-0 font-medium text-xs w-fit ${
                                lead.market === 'US' 
                                  ? 'bg-blue-100 text-blue-700' 
                                  : lead.market === 'BR' 
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {lead.market}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </td>
                  )}
                  {visibleColumns.contact && (
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {lead.email && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Mail className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate max-w-[200px]">{lead.email}</span>
                          </div>
                        )}
                        {lead.phone && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Phone className="w-4 h-4 flex-shrink-0" />
                            <span>{formatPhoneNumber(lead.phone)}</span>
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                  {visibleColumns.company && (
                    <td className="px-6 py-4">
                      {lead.company ? (
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Building className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate max-w-[150px]">{lead.company}</span>
                        </div>
                      ) : (
                        <span className="text-slate-400 text-sm">-</span>
                      )}
                    </td>
                  )}
                  {visibleColumns.status && (
                    <td className="px-6 py-4">
                      <Badge className={`${statusColors[lead.status]} border-0 font-medium pointer-events-none`}>
                        {lead.status}
                      </Badge>
                    </td>
                  )}
                  {visibleColumns.source && (
                    <td className="px-6 py-4">
                      <span className="text-sm text-slate-600 capitalize">
                        {lead.source?.replace(/_/g, ' ') || '-'}
                      </span>
                    </td>
                  )}
                  {visibleColumns.assignedTo && (
                    <td className="px-6 py-4">
                      {lead.assigned_to ? (
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <User className="w-3.5 h-3.5" />
                          {getAssignedUserName(lead.assigned_to)}
                        </div>
                      ) : (
                        <span className="text-sm text-slate-400">-</span>
                      )}
                    </td>
                  )}
                  {visibleColumns.createdBy && (
                    <td className="px-6 py-4">
                      {lead.created_by ? (
                        <div className="text-sm text-slate-600">
                          {getAssignedUserName(lead.created_by)}
                        </div>
                      ) : (
                        <span className="text-sm text-slate-400">-</span>
                      )}
                    </td>
                  )}
                  {visibleColumns.createdDate && (
                    <td className="px-6 py-4">
                      {createdDateTime ? (
                        <div className="text-sm">
                          <p className="text-slate-900 font-medium">
                            {createdDateTime.date}
                          </p>
                          <p className="text-xs text-slate-500">
                            {createdDateTime.time}
                          </p>
                        </div>
                      ) : (
                        <span className="text-sm text-slate-400">-</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
